<template>
  <Teleport to="body">
    <div v-if="ui.dmModalOpen" class="dm-mask" @click.self="close">
      <div class="dm-modal" role="dialog" aria-modal="true" aria-label="私信">
        <input ref="fileInput" type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden @change="onPickImage" />
        <div class="dm-sidebar" :class="{ hidden: !!activeConvId || !!draftUser }">
          <div class="dm-side-head">
            <span>私信</span>
            <button class="dm-x" type="button" aria-label="关闭" @click="close">✕</button>
          </div>
          <div v-if="loadingConv" class="dm-empty">加载中…</div>
          <div v-else-if="!conversations.length" class="dm-empty">还没有会话<br/>去帖子页点头像发起私信</div>
          <div v-else class="dm-conv-list">
            <div
              v-for="c in conversations"
              :key="c.id"
              class="dm-conv"
              :class="{ active: c.id === activeConvId }"
              @click="selectConv(c)"
            >
              <img class="dm-conv-avatar" :src="convAvatar(c)" alt="" />
              <div class="dm-conv-main">
                <div class="dm-conv-top">
                  <span class="dm-conv-name">{{ c.other?.username || '用户' }}</span>
                  <span class="dm-conv-time">{{ c.last_msg_at ? timeAgo(c.last_msg_at) : '' }}</span>
                </div>
                <div class="dm-conv-preview">{{ convPreview(c) }}</div>
              </div>
              <span v-if="c.unread > 0" class="dm-unread-dot">{{ c.unread > 99 ? '99+' : c.unread }}</span>
            </div>
          </div>
        </div>

        <div class="dm-thread" :class="{ hidden: !activeConvId && !draftUser }">
          <template v-if="activeConvId">
            <div class="dm-thread-head">
              <button class="dm-back" type="button" @click="backToList">‹</button>
              <img class="dm-thread-avatar" :src="threadAvatar" alt="" />
              <span class="dm-thread-name">{{ threadName }}</span>
              <button class="dm-x" type="button" aria-label="关闭" @click="close">✕</button>
            </div>
            <div ref="msgBox" class="dm-msgs" @scroll="onScroll">
              <div v-if="loadingMsg" class="dm-empty">加载中…</div>
              <button v-else-if="msgsHasMore" class="dm-load-older" :disabled="loadingMore" @click="loadOlder">
                {{ loadingMore ? '加载中…' : '查看更早消息' }}
              </button>
              <div
                v-for="m in messages"
                :key="m.id"
                class="dm-msg"
                :class="{ mine: isMine(m) }"
              >
                <img v-if="m.image_url" class="dm-msg-img" :data-imagebed-src="m.image_url" alt="图片" @click="previewImage = m.image_url" />
                <div v-if="m.content" class="dm-bubble">{{ m.content }}</div>
                <div class="dm-msg-time">{{ timeAgo(m.create_time) }}</div>
              </div>
            </div>
            <div v-if="pendingImage" class="dm-pending">
              <img :data-imagebed-src="pendingImage" alt="" />
              <button class="dm-pending-x" type="button" aria-label="移除图片" @click="pendingImage = ''">✕</button>
            </div>
            <div class="dm-input-bar">
              <button class="dm-attach" type="button" :disabled="uploading" :title="uploading ? '上传中…' : '发送图片'" @click="pickImage">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
              </button>
              <textarea
                ref="input"
                v-model="text"
                class="dm-input"
                rows="1"
                placeholder="发送私信…"
                maxlength="2000"
                @keydown.enter.exact.prevent="send"
              ></textarea>
              <button class="btn-primary dm-send" :disabled="sending || (!text.trim() && !pendingImage)" @click="send">{{ sending ? '发送中' : '发送' }}</button>
            </div>
          </template>
          <template v-else-if="draftUser">
            <div class="dm-thread-head">
              <button class="dm-back" type="button" @click="clearDraft">‹</button>
              <img class="dm-thread-avatar" :src="draftAvatar" alt="" />
              <span class="dm-thread-name">{{ draftUser.username }}</span>
              <button class="dm-x" type="button" aria-label="关闭" @click="close">✕</button>
            </div>
            <div class="dm-empty dm-draft-tip">向 {{ draftUser.username }} 发送第一条消息</div>
            <div v-if="pendingImage" class="dm-pending">
              <img :data-imagebed-src="pendingImage" alt="" />
              <button class="dm-pending-x" type="button" aria-label="移除图片" @click="pendingImage = ''">✕</button>
            </div>
            <div class="dm-input-bar">
              <button class="dm-attach" type="button" :disabled="uploading" :title="uploading ? '上传中…' : '发送图片'" @click="pickImage">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
              </button>
              <textarea
                ref="input"
                v-model="text"
                class="dm-input"
                rows="1"
                placeholder="发送私信…"
                maxlength="2000"
                @keydown.enter.exact.prevent="send"
              ></textarea>
              <button class="btn-primary dm-send" :disabled="sending || (!text.trim() && !pendingImage)" @click="send">{{ sending ? '发送中' : '发送' }}</button>
            </div>
          </template>
          <div v-else class="dm-empty dm-thread-empty">选择左侧会话开始聊天</div>
        </div>
      </div>
    </div>

    <!-- 图片预览 -->
    <div v-if="previewImage" class="dm-img-mask" @click.self="previewImage = ''">
      <button class="dm-img-close" type="button" aria-label="关闭" @click="previewImage = ''">✕</button>
      <img class="dm-img-large" :data-imagebed-src="previewImage" alt="" />
    </div>
  </Teleport>
</template>

<script>
import { getDmConversations, getDmMessages, sendDm, readDm, uploadImage } from '@/api'
import { useUiStore } from '@/store/ui'
import { useUserStore } from '@/store/user'
import { defaultAvatar } from '@/utils/avatar'
import { timeAgo } from '@/utils/format'

const POLL_MS = 8000

export default {
  name: 'DmModal',
  data() {
    return {
      ui: useUiStore(),
      userStore: useUserStore(),
      conversations: [],
      activeConvId: null,
      draftUser: null,
      messages: [],
      msgTotal: 0,
      msgPage: 1,
      size: 20,
      text: '',
      pendingImage: '',     // 待发送的图片 URL（pic 图床）
      uploading: false,
      previewImage: '',     // 点击放大的图片
      sending: false,
      loadingConv: false,
      loadingMsg: false,
      loadingMore: false,
      pollTimer: null,
    }
  },
  computed: {
    myId() { return Number(this.userStore.user?.id) || 0 },
    canUseDm() { return this.ui.dmModalOpen && this.userStore.isLogin },
    activeConv() { return this.conversations.find((c) => c.id === this.activeConvId) || null },
    threadName() { return this.activeConv?.other?.username || '' },
    threadAvatar() {
      const o = this.activeConv?.other
      return o?.avatar || defaultAvatar(o?.username, o?.id)
    },
    draftAvatar() {
      const d = this.draftUser
      return d?.avatar || defaultAvatar(d?.username, d?.id)
    },
    msgsHasMore() { return this.messages.length < this.msgTotal },
  },
  async mounted() {
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
    this.stopPoll()
  },
  watch: {
    canUseDm: {
      immediate: true,
      handler(canUse) {
        if (canUse) this.openDm()
        else this.resetDm()
      },
    },
    'ui.dmTargetUser'(target) {
      if (this.canUseDm && target && target.id) this.focusTargetUser(target)
    },
  },
  methods: {
    timeAgo,
    isMine(m) { return Number(m.sender_id) === this.myId },
    onKeydown(e) {
      if (e.key !== 'Escape') return
      if (this.previewImage) { this.previewImage = ''; return }
      this.close()
    },
    close() { this.ui.closeDmModal() },
    backToList() { this.activeConvId = null },
    clearDraft() { this.draftUser = null },
    async openDm() {
      await this.loadConversations()
      if (!this.canUseDm) return
      this.focusTargetUser(this.ui.dmTargetUser)
      this.startPoll()
      this.$nextTick(() => this.$refs.input?.focus?.())
    },
    resetDm() {
      this.stopPoll()
      this.conversations = []
      this.activeConvId = null
      this.draftUser = null
      this.messages = []
      this.msgTotal = 0
      this.msgPage = 1
      this.text = ''
      this.pendingImage = ''
      this.previewImage = ''
      this.sending = false
      this.loadingConv = false
      this.loadingMsg = false
      this.loadingMore = false
    },
    focusTargetUser(target) {
      if (!target || !target.id) return
      const exist = this.conversations.find((c) => Number(c.other?.id) === Number(target.id))
      if (exist) this.selectConv(exist)
      else {
        this.activeConvId = null
        this.draftUser = { id: Number(target.id), username: target.username, avatar: target.avatar }
      }
    },
    convAvatar(c) {
      const o = c.other
      return o?.avatar || defaultAvatar(o?.username, o?.id)
    },
    convPreview(c) {
      const m = c.last_message
      if (!m) return ''
      if (m.content) return m.content
      return m.image_url ? '[图片]' : ''
    },
    pickImage() { this.$refs.fileInput?.click?.() },
    async onPickImage(ev) {
      const file = ev.target.files && ev.target.files[0]
      if (!file) return
      const allow = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      if (!allow.includes(file.type)) {
        this.ui.showToast('仅支持 jpg/png/gif/webp 图片', 'error')
        ev.target.value = ''
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        this.ui.showToast('图片不能超过 10MB', 'error')
        ev.target.value = ''
        return
      }
      this.uploading = true
      try {
        const up = await uploadImage(file)
        this.pendingImage = up.data.url
      } catch (e) {
        this.ui.showToast(e.message || '图片上传失败', 'error')
      } finally {
        this.uploading = false
        ev.target.value = ''
      }
    },
    async loadConversations(silent = false) {
      if (!this.userStore.isLogin) {
        this.conversations = []
        this.loadingConv = false
        return
      }
      // 静默刷新（轮询）：不切 loading 态、不清空列表、失败不弹 toast，避免闪烁
      if (!silent) this.loadingConv = true
      try {
        this.conversations = (await getDmConversations()).data || []
      } catch (e) {
        if (!silent) this.ui.showToast(e.message || '加载失败', 'error')
      } finally {
        if (!silent) this.loadingConv = false
      }
    },
    async selectConv(c) {
      this.activeConvId = c.id
      this.draftUser = null
      await this.loadMessages(true)
      if (c.unread > 0) {
        try {
          const res = await readDm(c.id)
          c.unread = res.data?.unread ?? 0
          this.userStore.fetchDmUnread()
        } catch (e) { /* ignore */ }
      }
      this.$nextTick(() => { this.scrollToBottom(); this.$refs.input?.focus?.() })
    },
    async loadMessages(reset = false, silent = false) {
      if (!this.activeConvId) return
      if (reset && !silent) {
        // 首次/切换会话：清空 + 显示加载态
        this.msgPage = 1
        this.messages = []
        this.loadingMsg = true
      } else if (reset && silent) {
        // 轮询刷新：不清空、不切 loading，仅复用第 1 页
        this.msgPage = 1
      }
      try {
        const res = await getDmMessages(this.activeConvId, { page: this.msgPage, size: this.size })
        const list = res.data?.list || []
        this.msgTotal = res.data?.total ?? 0
        if (!reset) {
          // 加载更早历史：前置拼接
          this.messages = list.concat(this.messages)
        } else if (!silent) {
          // 首次加载：直接覆盖（此刻已为空）
          this.messages = list
          this.$nextTick(() => this.scrollToBottom())
        } else {
          // 轮询：仅当有新消息（末条 id 变大/条数变化）时才替换，避免无谓重渲染与滚动跳动
          const lastOld = this.messages.length ? this.messages[this.messages.length - 1].id : 0
          const lastNew = list.length ? list[list.length - 1].id : 0
          if (list.length !== this.messages.length || Number(lastNew) > Number(lastOld)) {
            this.messages = list
          }
        }
      } catch (e) {
        if (!silent) this.ui.showToast(e.message || '加载失败', 'error')
      } finally {
        if (!silent) this.loadingMsg = false
      }
    },
    async loadOlder() {
      if (this.loadingMore || !this.msgsHasMore) return
      this.loadingMore = true
      const box = this.$refs.msgBox
      const prevHeight = box ? box.scrollHeight : 0
      this.msgPage++
      try {
        await this.loadMessages(false)
        // 保持视口锚定在原位置（更早消息插入到顶部，scrollHeight 增大）
        this.$nextTick(() => { if (box) box.scrollTop = box.scrollHeight - prevHeight })
      } catch (e) { /* ignore */ } finally {
        this.loadingMore = false
      }
    },
    onScroll() {
      const box = this.$refs.msgBox
      if (!box) return
      if (box.scrollTop < 30 && this.msgsHasMore && !this.loadingMore) this.loadOlder()
    },
    async send() {
      const content = this.text.trim()
      const image = this.pendingImage
      if ((!content && !image) || this.sending) return
      const toId = this.activeConvId ? this.activeConv?.other?.id : this.draftUser?.id
      if (!toId) return
      this.sending = true
      try {
        const res = await sendDm(toId, content, image)
        const newConvId = Number(res.data?.conversation_id)
        const msg = res.data?.message
        if (!this.activeConvId) {
          // 新建会话：切到该会话并补全会话列表（静默，避免列表闪烁）
          this.activeConvId = newConvId
          this.draftUser = null
          await this.loadConversations(true)
        }
        if (msg) {
          this.messages.push(msg)
          this.msgTotal = Math.max(this.msgTotal, this.messages.length)
        }
        this.text = ''
        this.pendingImage = ''
        this.$nextTick(() => this.scrollToBottom())
        this.loadConversations(true)   // 静默刷新左侧最后消息/时间
      } catch (e) {
        this.ui.showToast(e.message || '发送失败', 'error')
      } finally {
        this.sending = false
      }
    },
    scrollToBottom() {
      const box = this.$refs.msgBox
      if (box) box.scrollTop = box.scrollHeight
    },
    startPoll() {
      this.stopPoll()
      this.pollTimer = setInterval(() => this.poll(), POLL_MS)
    },
    stopPoll() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null }
    },
    async poll() {
      if (!this.canUseDm) return
      // 静默刷新会话列表（未读角标/最后消息），不闪烁
      const prevUnread = this.conversations.reduce((s, c) => s + (c.unread || 0), 0)
      await this.loadConversations(true)
      const curActive = this.activeConv ? this.conversations.find((c) => c.id === this.activeConvId) : null
      // 当前会话：静默刷新消息，仅当有新消息且用户停在底部时滚到底
      if (curActive) {
        const box = this.$refs.msgBox
        const nearBottom = !box || (box.scrollHeight - box.scrollTop - box.clientHeight < 80)
        const before = this.messages.length
        await this.loadMessages(true, true)
        if (this.messages.length > before && nearBottom) this.$nextTick(() => this.scrollToBottom())
        // 仍有未读（对方刚发）则标记已读
        if (curActive.unread > 0) {
          try {
            const res = await readDm(curActive.id)
            curActive.unread = res.data?.unread ?? 0
            this.userStore.fetchDmUnread()
          } catch (e) { /* ignore */ }
        }
      }
      const curUnread = this.conversations.reduce((s, c) => s + (c.unread || 0), 0)
      if (curUnread !== prevUnread) this.userStore.fetchDmUnread()
    },
  },
}
</script>

<style scoped>
.dm-mask { position: fixed; inset: 0; background: rgba(16,24,40,.5); z-index: 170; display: flex; align-items: center; justify-content: center; padding: 24px; }
.dm-modal { display: flex; width: min(760px, 100%); height: min(560px, 90vh); background: var(--surface); border-radius: var(--radius-lg); box-shadow: var(--shadow-lg); overflow: hidden; }

.dm-sidebar { width: 250px; flex-shrink: 0; border-right: 1px solid var(--border); display: flex; flex-direction: column; min-height: 0; }
.dm-side-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; font-size: 15px; font-weight: 800; border-bottom: 1px solid var(--border); }
.dm-conv-list { flex: 1; overflow-y: auto; }
.dm-conv { display: flex; align-items: center; gap: 10px; padding: 10px 14px; cursor: pointer; border-bottom: 1px solid var(--border); }
.dm-conv:hover { background: var(--surface-2); }
.dm-conv.active { background: var(--primary-soft); }
.dm-conv-avatar { width: 38px; height: 38px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.dm-conv-main { flex: 1; min-width: 0; }
.dm-conv-top { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.dm-conv-name { font-size: 13.5px; font-weight: 600; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dm-conv-time { font-size: 11px; color: var(--text-3); flex-shrink: 0; }
.dm-conv-preview { font-size: 12px; color: var(--text-3); margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dm-unread-dot { min-width: 18px; height: 18px; padding: 0 5px; background: var(--danger); color: #fff; border-radius: 999px; font-size: 11px; font-weight: 700; line-height: 18px; text-align: center; flex-shrink: 0; }

.dm-thread { flex: 1; display: flex; flex-direction: column; min-width: 0; min-height: 0; }
.dm-thread-head { display: flex; align-items: center; gap: 10px; padding: 12px 14px; border-bottom: 1px solid var(--border); }
.dm-thread-avatar { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; }
.dm-thread-name { font-size: 14px; font-weight: 700; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dm-x, .dm-back { width: 28px; height: 28px; border-radius: 6px; border: none; background: none; color: var(--text-3); font-size: 16px; cursor: pointer; line-height: 1; }
.dm-x:hover, .dm-back:hover { background: var(--surface-2); color: var(--text); }
.dm-back { display: none; font-size: 20px; }

.dm-msgs { flex: 1; overflow-y: auto; padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; }
.dm-load-older { align-self: center; background: none; border: none; color: var(--text-3); font-size: 12px; cursor: pointer; padding: 4px 10px; }
.dm-load-older:hover { color: var(--primary); }
.dm-msg { display: flex; flex-direction: column; max-width: 72%; }
.dm-msg.mine { align-self: flex-end; align-items: flex-end; }
.dm-bubble { padding: 9px 13px; border-radius: 14px; font-size: 14px; line-height: 1.5; word-break: break-word; background: var(--surface-2); color: var(--text); }
.dm-msg.mine .dm-bubble { background: var(--primary); color: #fff; }
.dm-msg-time { font-size: 11px; color: var(--text-3); margin-top: 3px; }

.dm-input-bar { display: flex; align-items: flex-end; gap: 8px; padding: 10px 12px; border-top: 1px solid var(--border); }
.dm-input { flex: 1; resize: none; max-height: 96px; border: 1px solid var(--border-strong); border-radius: var(--radius-sm); padding: 8px 10px; font: inherit; font-size: 14px; color: var(--text); background: var(--surface-2); outline: none; }
.dm-input:focus { border-color: var(--primary); }
.dm-send { height: 38px; padding: 0 16px; }
.dm-attach { flex-shrink: 0; width: 38px; height: 38px; border-radius: var(--radius-sm); border: 1px solid var(--border); background: var(--surface-2); color: var(--text-2); cursor: pointer; display: grid; place-items: center; transition: color .15s, border-color .15s; }
.dm-attach:hover:not(:disabled) { color: var(--primary); border-color: var(--primary); }
.dm-attach:disabled { opacity: .6; cursor: progress; }
.dm-attach svg { width: 20px; height: 20px; }

/* 待发送图片预览 */
.dm-pending { display: flex; align-items: flex-end; gap: 8px; padding: 8px 12px 0; }
.dm-pending img { width: 120px; height: 120px; object-fit: cover; border-radius: var(--radius-sm); border: 1px solid var(--border); }
.dm-pending-x { width: 22px; height: 22px; border-radius: 50%; border: none; background: var(--danger); color: #fff; font-size: 12px; line-height: 1; cursor: pointer; }

/* 消息内图片 */
.dm-msg-img { max-width: 220px; max-height: 220px; border-radius: 10px; object-fit: cover; cursor: zoom-in; display: block; }

/* 图片大图预览 */
.dm-img-mask { position: fixed; inset: 0; background: rgba(0,0,0,.82); z-index: 200; display: flex; align-items: center; justify-content: center; padding: 24px; }
.dm-img-close { position: absolute; top: 16px; right: 16px; width: 40px; height: 40px; border-radius: 50%; color: #fff; background: rgba(255,255,255,.12); border: none; font-size: 22px; line-height: 1; cursor: pointer; }
.dm-img-close:hover { background: rgba(255,255,255,.2); }
.dm-img-large { max-width: min(1000px, 100%); max-height: 100%; border-radius: var(--radius-sm); object-fit: contain; }

.dm-empty { flex: 1; display: flex; align-items: center; justify-content: center; text-align: center; color: var(--text-3); font-size: 13px; line-height: 1.7; padding: 20px; }
.dm-thread-empty { }
.dm-draft-tip { }

@media (max-width: 640px) {
  .dm-mask { padding: 0; }
  .dm-modal { width: 100%; height: 100%; border-radius: 0; }
  .dm-back { display: grid; place-items: center; }
  /* 窄屏单栏：选中会话时隐藏列表；列表态隐藏会话窗 */
  .dm-sidebar.hidden { display: none; }
  .dm-thread.hidden { display: none; }
}
</style>
